/**
 * Sketchplan AI service.
 *
 * All AI calls were originally made directly to Google Gemini from the
 * browser, which required `process.env.API_KEY` to be baked into the public
 * JS bundle. They now route through the `process-image` Supabase Edge
 * Function so the Gemini key stays server-side.
 *
 * Exported function signatures are preserved exactly so the rest of the app
 * (App.tsx, etc.) is unchanged. Vector extraction now returns parsed JSON
 * built from the edge function's text response, matching the previous
 * client-side parsing behaviour.
 *
 * To roll back to direct client-side Gemini calls, restore this file from
 * git and re-add the `process.env.API_KEY` define in `vite.config.ts`.
 */

import {
  supabase,
  processImageServerSide,
  convertWithReferenceServerSide,
  extractVectorsServerSide,
} from './supabase';
import { ARCHITECT_SYSTEM_PROMPT } from '../constants';

// Sketch analysis result for pricing
export interface SketchAnalysisResult {
  roomCount: number;
  estimatedSqFt: number;
  complexity: 'simple' | 'moderate' | 'complex';
  hasMultipleFloors: boolean;
  features: string[];
  confidence: 'low' | 'medium' | 'high';
}

// Vector floor plan data structure
export interface VectorFloorPlanResult {
  width: number;
  height: number;
  walls: Array<{
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    thickness: number;
  }>;
  labels: Array<{
    id: string;
    x: number;
    y: number;
    text: string;
    fontSize?: number;
  }>;
}

async function getCurrentUser(): Promise<{ id?: string; email?: string }> {
  try {
    const { data } = await supabase.auth.getUser();
    return { id: data.user?.id, email: data.user?.email ?? undefined };
  } catch {
    return {};
  }
}

/**
 * Sends an image (with any burnt-in mask) for editing via the edge function.
 */
export const declutterImage = async (
  imageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1'
): Promise<string> => {
  const { id, email } = await getCurrentUser();
  return processImageServerSide(imageBase64, promptText, aspectRatio, 'declutter', id, email);
};

/**
 * Converts a floor plan sketch using a reference image for style.
 */
export const convertFloorPlanWithReference = async (
  sketchBase64: string,
  referenceBase64: string,
  promptText: string,
  aspectRatio: string = '1:1'
): Promise<string> => {
  const { id, email } = await getCurrentUser();
  return convertWithReferenceServerSide(
    sketchBase64,
    referenceBase64,
    promptText,
    aspectRatio,
    id,
    email
  );
};

/**
 * Upscale an image to 4K via the edge function.
 */
export const upscaleImage = async (imageBase64: string): Promise<string> => {
  const { id, email } = await getCurrentUser();
  // The edge function ignores `prompt` for `upscale`, but we pass an empty
  // string for parity with other actions.
  return processImageServerSide(imageBase64, '', '1:1', 'upscale', id, email);
};

// ============================================================================
// FLOOR PLAN INPAINTING — DEPRECATED
// ============================================================================

export interface InpaintSelection {
  /** X position as percentage (0-1) */
  x: number;
  /** Y position as percentage (0-1) */
  y: number;
  /** Width as percentage (0-1) */
  width: number;
  /** Height as percentage (0-1) */
  height: number;
}

/**
 * Floor-plan inpainting was previously a direct Gemini call. It is no longer
 * used by the app — the actual inpaint flow goes through the
 * `replicate-inpaint` edge function (SDXL with mask). Calling this stub
 * keeps the export surface stable for any external imports but throws
 * loudly so callers migrate.
 */
export const inpaintFloorPlan = async (
  _imageBase64: string,
  _selection: InpaintSelection | null,
  _prompt: string
): Promise<string> => {
  throw new Error(
    'inpaintFloorPlan() is no longer supported in the client. Use the `replicate-inpaint` edge function instead.'
  );
};

/**
 * Extracts vector floor plan data from an image. Routes through the
 * `process-image` edge function with `action: extract_vectors`. The edge
 * function returns the raw Gemini text response which we parse, validate,
 * and auto-scale here (same logic as the original client-side version).
 */
export const extractFloorPlanVectors = async (
  imageBase64: string,
  imageWidth: number,
  imageHeight: number
): Promise<VectorFloorPlanResult> => {
  const { id, email } = await getCurrentUser();
  const textContent = await extractVectorsServerSide(
    imageBase64,
    imageWidth,
    imageHeight,
    ARCHITECT_SYSTEM_PROMPT,
    id,
    email
  );

  console.log('='.repeat(60));
  console.log('RAW GEMINI RESPONSE (via edge function):');
  console.log(textContent);
  console.log('='.repeat(60));

  // Strip optional markdown code fences
  let jsonStr = textContent.trim();
  if (jsonStr.startsWith('```json')) {
    jsonStr = jsonStr.replace(/^```json\n?/, '').replace(/\n?```$/, '');
  } else if (jsonStr.startsWith('```')) {
    jsonStr = jsonStr.replace(/^```\n?/, '').replace(/\n?```$/, '');
  }

  let parsed: VectorFloorPlanResult;
  try {
    parsed = JSON.parse(jsonStr) as VectorFloorPlanResult;
  } catch (parseError) {
    console.error('Failed to parse Gemini JSON response:', parseError);
    console.error('Raw response:', textContent);
    throw new Error('Failed to parse floor plan vector data from AI response');
  }

  if (!parsed.walls) parsed.walls = [];
  if (!parsed.labels) parsed.labels = [];
  if (!parsed.width) parsed.width = imageWidth;
  if (!parsed.height) parsed.height = imageHeight;

  // Auto-scaling: detect normalised (0-1) coords or different source canvas
  const allWallCoords = parsed.walls.flatMap(w => [w.x1, w.y1, w.x2, w.y2]);
  const allLabelCoords = parsed.labels.flatMap(l => [l.x, l.y]);
  const allCoords = [...allWallCoords, ...allLabelCoords];
  const maxCoord = Math.max(...allCoords, 1);

  let scaleX = 1;
  let scaleY = 1;
  if (maxCoord <= 1.0 && allCoords.length > 0) {
    scaleX = imageWidth;
    scaleY = imageHeight;
  } else if (
    parsed.width &&
    parsed.height &&
    (parsed.width !== imageWidth || parsed.height !== imageHeight)
  ) {
    scaleX = imageWidth / parsed.width;
    scaleY = imageHeight / parsed.height;
  }

  parsed.walls = parsed.walls.map((wall, idx) => ({
    ...wall,
    id: wall.id || `w${idx + 1}`,
    x1: Math.max(0, Math.min(imageWidth, wall.x1 * scaleX)),
    y1: Math.max(0, Math.min(imageHeight, wall.y1 * scaleY)),
    x2: Math.max(0, Math.min(imageWidth, wall.x2 * scaleX)),
    y2: Math.max(0, Math.min(imageHeight, wall.y2 * scaleY)),
    thickness: wall.thickness || 6,
  }));

  parsed.labels = parsed.labels.map((label, idx) => ({
    ...label,
    id: label.id || `l${idx + 1}`,
    x: Math.max(0, Math.min(imageWidth, label.x * scaleX)),
    y: Math.max(0, Math.min(imageHeight, label.y * scaleY)),
    fontSize: label.fontSize || 14,
  }));

  parsed.width = imageWidth;
  parsed.height = imageHeight;

  console.log(
    `Extracted ${parsed.walls.length} walls and ${parsed.labels.length} labels for ${imageWidth}x${imageHeight} canvas`
  );

  return parsed;
};

/**
 * Sketch analysis is currently unused in the app. Kept as a stub so any
 * legacy imports keep type-checking; switch to a server-side call if/when
 * you need to revive this feature (the previous client-side version baked
 * the Gemini key into the bundle and is no longer safe to ship).
 */
export const analyzeSketch = async (
  _imageBase64: string
): Promise<SketchAnalysisResult> => {
  console.warn(
    'analyzeSketch() is deprecated; returning safe defaults. Implement an edge-function variant if you need real analysis.'
  );
  return {
    roomCount: 5,
    estimatedSqFt: 1000,
    complexity: 'moderate',
    hasMultipleFloors: false,
    features: [],
    confidence: 'low',
  };
};
